/**
 * Certificate Administration — admin landing.
 * Tabs:
 *  • Available  (all cert templates in catalog, regardless of any student)
 *  • Eligible   (investigators who finished required courses with ≥80%)
 *  • Pending    (PENDING_APPROVAL records awaiting admin issuance)
 *  • Issued     (active issued certs)
 *  • Revoked    (revoked history)
 *
 * Clicking Preview / Issue navigates to /admin/certifications/preview/:certId
 * (a FULL PAGE — no popups), per the user's UX requirements.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../config/canonical';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Award, Send, Download, Pencil, ShieldOff, Loader2, RefreshCw, Eye, BookOpen, Users, FileText, FileCheck2, Trash,
} from 'lucide-react';
import { toast } from 'sonner';

const TOKEN_KEY = 'nexus_token';
const apiCall = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const TABS = [
  { id: 'available', label: 'Available', icon: BookOpen },
  { id: 'eligible',  label: 'Eligible',  icon: Users },
  { id: 'pending',   label: 'Pending',   icon: FileText },
  { id: 'issued',    label: 'Issued',    icon: Award },
  { id: 'revoked',   label: 'Revoked',   icon: ShieldOff },
];

export default function PendingCertificates() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('available');
  const [catalog, setCatalog] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [pending, setPending] = useState([]);
  const [issued, setIssued] = useState([]);
  const [revoked, setRevoked] = useState([]);
  const [loading, setLoading] = useState(true);

  const counts = {
    available: catalog.length,
    eligible: eligible.length,
    pending: pending.length,
    issued: issued.length,
    revoked: revoked.length,
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, elig, pend, all] = await Promise.all([
        apiCall('/api/training-center/certifications').catch(() => ({ catalog: [] })),
        apiCall('/api/training-center/admin/certificates/eligible-scan').catch(() => ({ eligible: [] })),
        apiCall('/api/training-center/admin/certificates/pending').catch(() => ({ pending: [] })),
        apiCall('/api/training-center/admin/certificates').catch(() => ({ certificates: [] })),
      ]);
      setCatalog(cat.catalog || cat || []);
      setEligible(elig.eligible || []);
      setPending(pend.pending || []);
      const certs = all.certificates || [];
      setIssued(certs.filter(c => c.status === 'ISSUED'));
      setRevoked(certs.filter(c => c.status === 'REVOKED'));
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const approveCertificate = async (cert) => {
    if (!window.confirm(`Approve & issue ${cert.cert_title || cert.id} to ${cert.user_name}?`)) return;
    try {
      await apiCall(`/api/training-center/admin/certificates/${cert.id}/approve`, { method: 'POST' });
      toast.success('Certificate approved and issued');
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const deleteCertificate = async (cert) => {
    if (!window.confirm(`Delete certificate ${cert.id}? This cannot be undone.`)) return;
    try {
      await apiCall(`/api/training-center/admin/certificates/${cert.id}`, { method: 'DELETE' });
      toast.success('Certificate deleted');
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="pending-certs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Award className="w-6 h-6 text-amber-300" /> Certificate Administration
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Manage the 8-track Nexus academy certifications · Manual issuance · Exact template preview
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={fetchAll} variant="outline" className="border-slate-600 !text-slate-200" data-testid="certs-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={() => navigate('/admin/course-builder')}
                  className="bg-cyan-600 hover:bg-cyan-500 !text-white font-semibold"
                  data-testid="certs-open-course-builder">
            <BookOpen className="w-4 h-4 mr-1.5" /> Course Builder
          </Button>
          <Button onClick={() => navigate('/admin/certificate-editor')}
                  className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
                  data-testid="certs-add-new">
            <Award className="w-4 h-4 mr-1.5" /> Add Certificate
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1 bg-slate-900/60 border border-slate-700 rounded-lg p-1">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`certs-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded text-xs font-semibold ${
                active ? 'bg-amber-500 text-slate-900' : 'text-slate-300 hover:bg-slate-800'
              }`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded font-mono text-[10px] ${active ? 'bg-slate-900/20 text-slate-900' : 'bg-slate-800 text-slate-400'}`}>
                {counts[t.id]}
              </span>
            </button>
          );
        })}
      </div>

      {loading && <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}

      {!loading && tab === 'available' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="certs-available-grid">
          {catalog.length === 0 && <p className="text-slate-500 italic">No cert templates in catalog.</p>}
          {catalog.map(c => {
            const issuedCount = issued.filter(x => x.cert_id === c.id).length;
            const eligibleCount = eligible.filter(x => x.cert_id === c.id).length;
            return (
              <Card key={c.id} className="bg-[#131A2A] border-slate-700" data-testid={`avail-card-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-white">{c.title}</h3>
                      <p className="text-[11px] font-mono text-slate-400">{c.id}</p>
                    </div>
                    <Badge className="bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px] font-mono">
                      {c.passing_score || 80}% pass
                    </Badge>
                  </div>
                  {c.summary && <p className="text-xs text-slate-300 mt-2">{c.summary}</p>}
                  <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                    <div className="bg-slate-800/50 rounded px-2 py-1.5">
                      <p className="text-slate-400 uppercase tracking-wider">Required</p>
                      <p className="text-white font-mono text-[10px]">{(c.course_ids || []).join(', ')}</p>
                    </div>
                    <div className="bg-slate-800/50 rounded px-2 py-1.5">
                      <p className="text-slate-400 uppercase tracking-wider">Eligible / Issued</p>
                      <p className="text-emerald-300 font-bold">{eligibleCount} <span className="text-slate-500">·</span> {issuedCount}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                    <Button onClick={() => navigate(`/admin/certifications/preview/template-${c.id}?cert_id=${c.id}`)}
                      size="sm" variant="outline" className="border-slate-500 !text-slate-200" data-testid={`avail-preview-${c.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Preview Template
                    </Button>
                    <Button onClick={() => setTab('eligible')}
                      size="sm" variant="outline" className="border-emerald-500/40 !text-emerald-300" data-testid={`avail-view-eligible-${c.id}`}>
                      <Users className="w-3.5 h-3.5 mr-1" /> View Eligible
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && tab === 'eligible' && (
        <div className="space-y-2" data-testid="certs-eligible-list">
          {eligible.length === 0 ? (
            <p className="text-slate-500 italic px-2">No investigators are currently eligible. Once a user completes every required course with ≥80%, they appear here.</p>
          ) : eligible.map(row => (
            <Card key={`${row.user_id}-${row.cert_id}`} className="bg-[#131A2A] border-slate-700"
              data-testid={`elig-row-${row.user_id}-${row.cert_id}`}>
              <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white">{row.user_name}</p>
                  <p className="text-[11px] text-slate-400">
                    <span className="text-amber-300 font-mono">{row.cert_id}</span> · {row.cert_title}
                  </p>
                  <p className="text-[10px] text-emerald-300 mt-0.5 font-mono">All {row.required_courses.length} courses passed · {row.existing_status ? `Existing: ${row.existing_status}` : 'No record yet'}</p>
                </div>
                <Button onClick={() => navigate(
                  row.existing_id
                    ? `/admin/certifications/preview/${row.existing_id}`
                    : `/admin/certifications/preview/ELIG-${row.user_id}-${row.cert_id}?user_id=${row.user_id}&cert_id=${row.cert_id}`
                )} size="sm" className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid={`elig-issue-${row.user_id}-${row.cert_id}`}>
                  <Send className="w-3.5 h-3.5 mr-1" /> Preview & Issue
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && (tab === 'pending' || tab === 'issued' || tab === 'revoked') && (() => {
        const rows = tab === 'pending' ? pending : tab === 'issued' ? issued : revoked;
        if (rows.length === 0) return <p className="text-slate-500 italic px-2">No {tab} certificates.</p>;
        return (
          <div className="space-y-2" data-testid={`certs-${tab}-list`}>
            {rows.map(cert => (
              <Card key={cert.id} className="bg-[#131A2A] border-slate-700" data-testid={`cert-row-${cert.id}`}>
                <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white">{cert.user_name}</p>
                    <p className="text-[11px] text-slate-400">
                      <span className="text-amber-300 font-mono">{cert.id}</span> · {cert.cert_title}
                    </p>
                    {cert.issued_at && (
                      <p className="text-[10px] text-slate-500 mt-0.5">Issued {new Date(cert.issued_at).toLocaleString()}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Button onClick={() => navigate(`/admin/certifications/preview/${cert.id}`)}
                      size="sm" variant="outline" className="border-slate-500 !text-slate-200" data-testid={`cert-preview-${cert.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                    </Button>
                    {(tab === 'issued' || tab === 'pending') && (
                      <Button onClick={() => navigate(`/admin/certifications/preview/${cert.id}`)}
                        size="sm" variant="outline" className="border-slate-500 !text-slate-200" data-testid={`cert-edit-${cert.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    )}
                    {tab === 'pending' && (
                      <>
                        <Button onClick={() => approveCertificate(cert)}
                          size="sm" className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold" data-testid={`cert-approve-${cert.id}`}>
                          <FileCheck2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button onClick={() => navigate(`/admin/certifications/preview/${cert.id}`)}
                          size="sm" className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid={`cert-issue-${cert.id}`}>
                          <Send className="w-3.5 h-3.5 mr-1" /> Issue
                        </Button>
                      </>
                    )}
                    {tab !== 'revoked' && (
                      <Button onClick={() => deleteCertificate(cert)}
                        size="sm" variant="outline" className="border-red-500/40 !text-red-300 hover:bg-red-500/10" data-testid={`cert-delete-${cert.id}`}>
                        <Trash className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );
      })()}
    </div>
  );
}
